import type { LiveDeliveryMode } from './liveDelivery';

export type LiveArchiveMode = 'off' | 'standard' | 'production';

export interface LiveArchivePolicy {
  mode: LiveArchiveMode;
  archiveEnabled: boolean;
  dvrEnabled: boolean;
  clippingEnabled: boolean;
  autoPublishReplay: boolean;
  retentionDays: number;
  clipWindowMinutes: number;
  summary: string;
}

type LiveArchiveSource = 'native' | 'shorts' | 'meeting' | 'event' | 'battle' | 'audio';

interface ResolveLiveArchiveModeOptions {
  source?: LiveArchiveSource;
  deliveryMode?: LiveDeliveryMode;
  archiveMode?: LiveArchiveMode;
  archivePolicy?: Partial<LiveArchivePolicy> | null;
}

interface ResolveLiveArchivePolicyOptions extends ResolveLiveArchiveModeOptions {
  autoPublishReplay?: boolean;
}

export const LIVE_ARCHIVE_MODE_OPTIONS: Array<{
  mode: LiveArchiveMode;
  label: string;
  badge: string;
  description: string;
}> = [
  {
    mode: 'off',
    label: 'Live Only',
    badge: 'No Archive',
    description: 'No DVR, replay, or clip contract is reserved for this live session.',
  },
  {
    mode: 'standard',
    label: 'Standard Replay',
    badge: '30d DVR',
    description: 'Reserves archive, DVR, and clip readiness for regular public broadcasts.',
  },
  {
    mode: 'production',
    label: 'Production Archive',
    badge: '90d DVR',
    description: 'Longer retention and a wider clip window for planned productions and encoder workflows.',
  },
];

export const resolveLiveArchiveMode = ({
  source,
  deliveryMode,
  archiveMode,
  archivePolicy,
}: ResolveLiveArchiveModeOptions): LiveArchiveMode => {
  if (archiveMode) return archiveMode;

  const policyMode = archivePolicy?.mode;
  if (policyMode === 'off' || policyMode === 'standard' || policyMode === 'production') {
    return policyMode;
  }

  if (deliveryMode === 'quality') {
    return 'production';
  }

  if (source === 'event' || source === 'native' || deliveryMode === 'balanced') {
    return 'standard';
  }

  return 'off';
};

export const resolveLiveArchivePolicy = ({
  source,
  deliveryMode,
  archiveMode,
  archivePolicy,
  autoPublishReplay: _autoPublishReplay,
}: ResolveLiveArchivePolicyOptions): LiveArchivePolicy => {
  const mode = resolveLiveArchiveMode({
    source,
    deliveryMode,
    archiveMode,
    archivePolicy,
  });
  switch (mode) {
    case 'production':
      return {
        mode,
        archiveEnabled: true,
        dvrEnabled: true,
        clippingEnabled: true,
        autoPublishReplay: false,
        retentionDays: 90,
        clipWindowMinutes: 60,
        summary: 'Production archive policy with replay review, DVR readiness, and an extended clip window.',
      };
    case 'standard':
      return {
        mode,
        archiveEnabled: true,
        dvrEnabled: true,
        clippingEnabled: true,
        autoPublishReplay: false,
        retentionDays: 30,
        clipWindowMinutes: 30,
        summary: 'Standard replay policy with host review before publish, plus DVR and clip readiness for regular public broadcasts.',
      };
    default:
      return {
        mode: 'off',
        archiveEnabled: false,
        dvrEnabled: false,
        clippingEnabled: false,
        autoPublishReplay: false,
        retentionDays: 0,
        clipWindowMinutes: 0,
        summary: 'Live-only session with no replay, DVR, or clip readiness reserved.',
      };
  }
};
